import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export const borrowerProfileAPI = createApi({
  reducerPath: 'borrower-profile',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['checkProfileCompletion', 'getApplication', 'getHousingExpense', 'fundmoreSyncState', 'creditReport'],
  endpoints: (builder) => ({
    updateStatus: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/dashboard/status_admin/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getBorrowerProfile: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),

    getApplicationCoborrowers: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loans/applications/${applicationId}/coborrowers`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),

    getBorrowerPersonalDetails: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loans/applications/${applicationId}/borrower/${borrowerId}/personal-details`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: (result, error, { borrowerId }) => [
        { type: 'getApplication', id: borrowerId },
      ],
    }),

    addB2BSpouseCoBorrower: builder.mutation({
      query: ({ applicationId, firstName, lastName }) => ({
        url: `/b2b/loans/applications/${applicationId}/coborrowers/add-spouse`,
        method: 'POST',
        body: { firstName, lastName },
        headers: getHeaders(),
      }),
      invalidatesTags: ['getApplication'],
    }),

    inviteB2BCoBorrower: builder.mutation({
      query: ({ applicationId, firstName, lastName, email, phoneNumber, relationship }) => ({
        url: `/b2b/loans/applications/${applicationId}/coborrowers/invite`,
        method: 'POST',
        body: { firstName, lastName, email, phoneNumber, relationship },
        headers: getHeaders(),
      }),
      invalidatesTags: ['getApplication'],
    }),

    removeB2BCoBorrower: builder.mutation({
      query: ({ applicationId, coborrowerId }) => ({
        url: `/b2b/loans/applications/${applicationId}/coborrowers/${coborrowerId}`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: ['getApplication'],
    }),

    updatePersonalDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/dashboard/personal_details/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateLoanDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/dashboard/loan/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['checkProfileCompletion', 'getApplication'],
    }),

    updateEmployeeAndIncome: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateAssets: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/assets`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateLiabilities: builder.mutation({
      query: ({ applicationId, data, accounts, borrowerId }) => {
        const resolvedBorrowerId =
          borrowerId || data?.borrowerId || accounts?.[0]?.borrowerId;
        const borrowerParam = resolvedBorrowerId
          ? `?borrowerId=${resolvedBorrowerId}`
          : '';
        let accountsList;
        if (accounts) {
          accountsList = accounts.map(({ borrowerId: _bId, ...rest }) => rest);
        } else {
          const { borrowerId: _bId, ...accountData } = data || {};
          accountsList = [accountData];
        }
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}${borrowerParam}`,
          method: 'PUT',
          body: { accounts: accountsList },
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateREOSDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateDeclaration: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/dashboard/declaration/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    updateDemographics: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/loan/dashboard/demographics/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    createPersonalDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    createLoanDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/loan`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['checkProfileCompletion'],
    }),

    createEmploymentAndIncome: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `/b2b/loan/loa/assets`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createLiabilities: builder.mutation({
      query: ({ applicationId, data, borrowerId }) => {
        const resolvedBorrowerId = borrowerId || data?.borrowerId;
        const borrowerParam = resolvedBorrowerId
          ? `?borrowerId=${resolvedBorrowerId}`
          : '';
        const { borrowerId: _bId, ...accountData } = data || {};
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}${borrowerParam}`,
          method: 'PUT',
          body: { accounts: [accountData] },
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createREOSData: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createDeclaration: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/declaration`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    createDemographics: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/dashboard/demographics`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),
    checkProfileCompletion: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/application/loan_info/complete/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['checkProfileCompletion'],
    }),
    //delete existing dynamic employee
    deleteEmploymentDetails: builder.mutation({
      query: ({ employmentIncomeId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/employment_and_income/${employmentIncomeId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //get liabilities from normalized table
    getLiabilities: builder.query({
      query: ({ applicationId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}${borrowerId ? `?borrowerId=${borrowerId}` : ''}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),
    //delete existing dynamic liability
    deleteLiabilitiesDetails: builder.mutation({
      query: ({ applicationId, userLiabilityId, borrowerId }) => {
        return {
          url: `/b2b/loan-management/liabilities/${applicationId}/${userLiabilityId}${borrowerId ? `?borrowerId=${borrowerId}` : ''}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //delete existing dynamic REO
    deleteReosDetails: builder.mutation({
      query: ({ reosId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/reos/${reosId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //delete existing dynamic Assets
    deleteAssetsDetails: builder.mutation({
      query: ({ assetId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/assets/${assetId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    getBorrowerProfileAdmin: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getApplication'],
    }),

    // Per-section lightweight GET endpoints
    getPersonalDetailsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/personal_details/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getLoanDetailsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/loan_details/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getEmploymentSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/employment_and_income/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getAssetsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/assets/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getLiabilitiesSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/liabilities/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getReosSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/reos/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getDeclarationsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/declarations/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),
    getDemographicsSection: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/section/demographics/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),

    getBorrowerChatContext: builder.query({
      query: () => {
        return {
          url: `b2c/ai/chat/borrower-context`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    updateEmployeeAndIncomeAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateAssetsAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/assets`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateLiabilitiesAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    updateREOSDetailsAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createEmploymentAndIncomeAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createAssetDetailsAdmin: builder.mutation({
      query: (data) => {
        return {
          url: `/b2b/loan/loa/assets`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createLiabilitiesAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    createREOSDataAdmin: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/loa/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),

    //delete existing dynamic employee
    deleteEmploymentDetailsAdmin: builder.mutation({
      query: ({ employmentIncomeId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/employment_and_income/${employmentIncomeId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //delete existing dynamic liability
    deleteLiabilitiesDetailsAdmin: builder.mutation({
      query: ({ userLiabilityId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/liabilities/${userLiabilityId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //delete existing dynamic REO
    deleteReosDetailsAdmin: builder.mutation({
      query: ({ reosId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/reos/${reosId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    //delete existing dynamic Assets
    deleteAssetsDetailsAdmin: builder.mutation({
      query: ({ assetId, userId }) => {
        return {
          url: `/b2b/loan/dashboard/assets/${assetId}?user_id=${userId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getApplication'],
    }),
    getHousingExpense: builder.query({
      query: ({ userId }) => {
        return {
          url: `b2c/loan/dashboard/housing_expense/${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getHousingExpense'],
    }),
    getHousingExpenseOfficer: builder.query({
      query: ({ userId, applicationId }) => {
        const params = applicationId ? `?application_id=${applicationId}` : '';
        return {
          url: `/b2b/loan/loa/housing_expense/${userId || 'none'}${params}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getHousingExpense'],
    }),

    saveHousingExpense: builder.mutation({
      query: ({ data, userId }) => {
        return {
          url: `b2c/loan/dashboard/housing_expense/${userId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getHousingExpense'],
    }),

    saveHousingExpenseOfficer: builder.mutation({
      query: ({ data, userId }) => {
        return {
          url: `/b2b/loan/loa/housing_expense/${userId}`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getHousingExpense'],
    }),

    getTransactionDetailsOfficer: builder.query({
      query: ({ userId, applicationId }) => {
        const params = applicationId ? `?application_id=${applicationId}` : '';
        return {
          url: `/b2b/loan/loa/transaction_details/${userId || 'none'}${params}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getTransactionDetails: builder.query({
      query: ({ userId }) => {
        return {
          url: `b2c/loan/dashboard/transaction_details/${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getIncomeAnalytics: builder.query({
      query: ({ userId }) => {
        return {
          url: `/b2b/ai/income-analytics?user_id=${userId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getIncomeDocumentStatus: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/income_analysis/check_income_document?applicationId=${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    updateIncomeAnalytics: builder.mutation({
      query: ({ updatedIncomeData }) => {
        return {
          url: `/b2b/loan/income_analysis/update_income_analytics`,
          method: 'PUT',
          body: updatedIncomeData,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getIncomeAnalytics'],
    }),

    //truv integration
    createTruvIntegration: builder.mutation({
      query: ({ data }) => {
        return {
          url: `/b2b/loan/truv_api/bridge-token`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getNewTruvLinkId: builder.mutation({
      query: ({ data }) => {
        console.log('data', data);
        return {
          url: `/b2b/loan/truv_api/link-id`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    getEmploymentDetails: builder.query({
      query: ({ linkId }) => {
        return {
          url: `/b2b/loan/truv_api/links/${linkId}/employment`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    createEmploymentReport: builder.mutation({
      query: ({ userId }) => {
        console.log('data', userId);
        return {
          url: `/b2b/loan/truv_api/users/${userId}/reports`,
          method: 'POST',
          body: {},
          headers: getHeaders(),
        };
      },
    }),

    // Create a new AI chat session (backend_v2-compatible)
    createNewChat: builder.mutation({
      query: ({
        borrower_id,
        loan_application_id,
        metadata,
        user_id,
        organization_id,
        isB2C = false,
      } = {}) => {
        const body = {};
        if (isB2C) {
          if (borrower_id) body.borrower_id = borrower_id;
          if (organization_id) body.organization_id = organization_id;
        } else {
          if (user_id) body.user_id = user_id;
          if (organization_id) body.organization_id = organization_id;
          if (borrower_id) body.borrower_id = borrower_id;
        }
        if (loan_application_id) body.loan_application_id = loan_application_id;
        if (metadata) body.metadata = metadata;

        return {
          url: isB2C ? `b2c/ai/chat/sessions` : `b2b/ai/chat/sessions`,
          method: 'POST',
          body,
          headers: getHeaders(),
        };
      },
    }),

    // List AI chat sessions for the current user
    getAllChats: builder.query({
      query: ({ isB2C = false } = {}) => ({
        url: isB2C ? `b2c/ai/chat/sessions` : `b2b/ai/chat/sessions`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // Fetch chat messages for a session
    getChatMessages: builder.query({
      query: ({ chatId, isB2C = false }) => ({
        url: isB2C
          ? `b2c/ai/chat/sessions/${chatId}/messages`
          : `b2b/ai/chat/sessions/${chatId}/messages`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),

    // Send a message to a chat session
    sendChatMessage: builder.mutation({
      query: ({ chatId, isB2C = false, payload }) => ({
        url: isB2C
          ? `b2c/ai/chat/sessions/${chatId}/messages`
          : `b2b/ai/chat/sessions/${chatId}/messages`,
        method: 'POST',
        body: payload,
        headers: getHeaders(),
      }),
    }),

    findBorrower: builder.query({
      query: ({ borrowerName }) => ({
        url: `/b2b/loan/loa/lead_info`,
        method: 'POST',
        body: {
          loanOfficerIds: [],
        },
        params: {
          page: 1,
          page_size: 1,
          search: borrowerName,
        },
        headers: getHeaders(),
      }),
      transformResponse: (response) => {
        if (response.data?.lead_information?.length > 0) {
          return {
            applicationId: response.data.lead_information[0].user_info.applicationId,
          };
        }
        return null;
      },
    }),

    syncFundmoreApplication: builder.mutation({
      query: ({ applicationId, uploadDocuments = true }) => {
        return {
          url: `/b2b/integrations/fundmore/sync/${applicationId}?upload_documents=${
            uploadDocuments ? 'true' : 'false'
          }`,
          method: 'POST',
          headers: getHeaders(),
        };
      },
      // Refresh sync state after a successful submission so the button
      // re-renders with the new "synced" / "drifted" / "failed" label.
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'fundmoreSyncState', id: applicationId },
      ],
    }),

    getFundmoreSyncState: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/integrations/fundmore/sync/${applicationId}/state`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'fundmoreSyncState', id: applicationId },
      ],
    }),

    getFundmoreSyncHistory: builder.query({
      query: ({ applicationId, limit = 50 }) => ({
        url: `/b2b/integrations/fundmore/sync/${applicationId}/history?limit=${limit}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'fundmoreSyncState', id: applicationId },
      ],
    }),

    getBorrowerSummary: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/dashboard/summary/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: ['getApplication'],
    }),

    // ----- Credit pull (Equifax via FundMore Manager Portal) ---------------
    pullCreditReport: builder.mutation({
      query: ({ applicationId, includeSpouse = false, includeOnlyApplicantId = null }) => ({
        url: `/b2b/loans/applications/${applicationId}/credit-report/pull`,
        method: 'POST',
        body: {
          include_spouse: includeSpouse,
          include_only_applicant_id: includeOnlyApplicantId,
        },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'creditReport', id: applicationId },
      ],
    }),

    getLatestCreditReport: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loans/applications/${applicationId}/credit-report`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'creditReport', id: applicationId },
      ],
    }),

    getCreditReportHistory: builder.query({
      query: ({ applicationId, limit = 50 }) => ({
        url: `/b2b/loans/applications/${applicationId}/credit-report/history?limit=${limit}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'creditReport', id: applicationId },
      ],
    }),

    // Lazy: returns a presigned URL the caller opens in a new tab.
    // Not cached — every download click should re-fetch in case the
    // URL has expired.
    getCreditReportPdfUrl: builder.mutation({
      query: ({ applicationId, reportId = null }) => ({
        url: reportId
          ? `/b2b/loans/applications/${applicationId}/credit-report/pdf?report_id=${reportId}`
          : `/b2b/loans/applications/${applicationId}/credit-report/pdf`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
  }),
});

export const {
  useCheckProfileCompletionQuery,
  useUpdateStatusMutation,
  //get api export LO
  useGetBorrowerProfileQuery,
  //update api export LO
  useUpdatePersonalDetailsMutation,
  useUpdateLoanDetailsMutation,
  useUpdateEmployeeAndIncomeMutation,
  useUpdateAssetsMutation,
  useUpdateLiabilitiesMutation,
  useUpdateREOSDetailsMutation,
  useUpdateDeclarationMutation,
  useUpdateDemographicsMutation,
  //create api export LO
  useCreatePersonalDetailsMutation,
  useCreateLoanDetailsMutation,
  useCreateAssetDetailsMutation,
  useCreateEmploymentAndIncomeMutation,
  useCreateREOSDataMutation,
  useCreateLiabilitiesMutation,
  useCreateDeclarationMutation,
  useCreateDemographicsMutation,
  //delete api export LO
  useDeleteEmploymentDetailsMutation,
  useDeleteLiabilitiesDetailsMutation,
  useDeleteReosDetailsMutation,
  useDeleteAssetsDetailsMutation,
  //get liabilities
  useGetLiabilitiesQuery,
  //get api export LOA
  useGetBorrowerProfileAdminQuery,
  //create api export LOA
  useCreateEmploymentAndIncomeAdminMutation,
  useCreateAssetDetailsAdminMutation,
  useCreateLiabilitiesAdminMutation,
  useCreateREOSDataAdminMutation,
  //update api export LOA
  useUpdateEmployeeAndIncomeAdminMutation,
  useUpdateAssetsAdminMutation,
  useUpdateLiabilitiesAdminMutation,
  useUpdateREOSDetailsAdminMutation,
  //delete api export LOA
  useDeleteEmploymentDetailsAdminMutation,
  useDeleteAssetsDetailsAdminMutation,
  useDeleteLiabilitiesDetailsAdminMutation,
  useDeleteReosDetailsAdminMutation,
  //Housing expense api export
  useGetHousingExpenseQuery,
  useGetHousingExpenseOfficerQuery,
  useSaveHousingExpenseMutation,
  useSaveHousingExpenseOfficerMutation,
  //transaction details api export
  useGetTransactionDetailsOfficerQuery,
  useGetTransactionDetailsQuery,
  // income analyt get api export
  useGetIncomeAnalyticsQuery,
  useGetIncomeDocumentStatusQuery,
  useUpdateIncomeAnalyticsMutation,
  //truv integration
  useCreateTruvIntegrationMutation,
  useGetNewTruvLinkIdMutation,
  useGetEmploymentDetailsQuery,
  useCreateEmploymentReportMutation,
  //ChatHistory
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
  useSendChatMessageMutation,
  useGetBorrowerChatContextQuery,
  useLazyFindBorrowerQuery,
  useSyncFundmoreApplicationMutation,
  useGetFundmoreSyncStateQuery,
  useGetFundmoreSyncHistoryQuery,
  usePullCreditReportMutation,
  useGetLatestCreditReportQuery,
  useGetCreditReportHistoryQuery,
  useGetCreditReportPdfUrlMutation,
  //Co-borrowers
  useGetApplicationCoborrowersQuery,
  useGetBorrowerPersonalDetailsQuery,
  useAddB2BSpouseCoBorrowerMutation,
  useInviteB2BCoBorrowerMutation,
  useRemoveB2BCoBorrowerMutation,
  //Per-section lightweight queries
  useGetPersonalDetailsSectionQuery,
  useGetLoanDetailsSectionQuery,
  useGetEmploymentSectionQuery,
  useGetAssetsSectionQuery,
  useGetLiabilitiesSectionQuery,
  useGetReosSectionQuery,
  useGetDeclarationsSectionQuery,
  useGetDemographicsSectionQuery,
  useGetBorrowerSummaryQuery,
} = borrowerProfileAPI;
